import React, { useEffect, useState } from "react";
import SaveButton from "../../../components/Buttons/SaveButton";
import DataTable from '../../../components/DataTable/DataTable';
import { IColInfo } from "../../../components/DataTable/Interfaces";
import SelectListField from "../../../components/Fields/SelectListField";
import { FormConfig } from "../../../components/Fields/Types";
import useSelectListOptions from "../../../components/Hooks/SelectListOptionsHook/useSelectListOptions";
import useValidation from "../../../components/Hooks/ValidationHook/useValidation";
import Popup from "../../../components/Popups/Popup";
import { ApplicationFunctions, useAuth } from "../../../contexts/authContext";
import { useNotifications } from "../../../contexts/notificationContext";
import ItemModel from "../../../models/ItemModel";
import ItemSizeModel from "../../../models/ItemSizeModel";
import DataService from "../../../services/data.service";
import { PopupProps } from "../../../types/PopupProps";
import { Result } from "../../../types/Result";

interface IProps extends PopupProps {
    item: ItemModel
    itemRevisionId: number
}

interface ItemSizeCreateModel {
    itemId: number
    itemRevisionId ?: number
    sizeSetId: number
    sizeId: number
}

const ItemSizesPopup: React.FC<IProps> = ({ show, onHide, item, itemRevisionId }) => {
    const [serverErrors, setServerErrors] = useState<Result>({ isSuccesful: true, messages: [] });
    const [tableData, setTableData] = useState<ItemSizeModel[]>();
    const [showAddSize, setShowAddSize] = useState(false);
    const { handleResult, handleException } = useNotifications();
    const { isAuthorizedFor } = useAuth();

    const [formData, setFormData] = useState<ItemSizeCreateModel>({
        itemId: item.id,
        itemRevisionId: itemRevisionId,
        sizeSetId: 0,
        sizeId: 0
    });

    const sizesets = useSelectListOptions('api/v1/selectlist/Sizesets?activeOnly=true', '-- Select sizeset');
    const sizes = useSelectListOptions(`api/v1/selectlist/Sizes?sizeSetId=${formData.sizeSetId}&activeOnly=true`, '-- Select size');

    useEffect(() => {
        getTableData();
    }, [item]);

    function getTableData() {
        DataService.get<any[]>('api/v1/ItemSizes/ByItemRevision/' + itemRevisionId)
            .then(response => {
                setTableData(response);
            })
            .catch(e => { handleException(e); });
    }

    function deleteRow(rowData) {
        DataService.delete('api/v1/ItemSizes/', rowData.id)
            .then(response => {
                handleResult(response);
                getTableData();
            })
            .catch(e => { handleException(e); });
    }

    function submit() {
        if (formData) {
            DataService
                .create<number>(`api/v1/ItemSizes`, formData)
                .then(response => {
                    handleResult(response);
                    setServerErrors(response);
                    if (response.isSuccesful === true) {
                        setShowAddSize(false);
                        getTableData();
                        formData.sizeId = 0;
                    }
                })
                .catch(e => handleException(e));
        }
    }

    const formConfig: FormConfig = {
        fields: [
            { name: 'sizeSetId', label: 'Size set', labelSize: 2, validationRules: [{ type: 'isRequired' }] },
            { name: 'sizeId', label: 'Size', labelSize: 2, validationRules: [{ type: 'isRequired' }] },
        ],
        handleSubmit: submit,
        showErrors: 'always',
    };

    const { getFieldProps, getSubmitButtonProps } = useValidation(formConfig, formData, serverErrors);

    const colInfos: IColInfo[] = [
        { key: true, dataProp: 'Id', visible: false },
        { title: 'Size set', dataProp: 'size.sizeSet.name', sort: 'asc' },
        { title: 'Size', dataProp: 'size.name' }
    ];

    if (isAuthorizedFor(ApplicationFunctions.UpdateItemRevision)) {
        colInfos.push(
            {
                headerButtons: [{ text: "Add", icon: 'fa-plus-square', color: '#26A69A', onClick: () => setShowAddSize(true) }],
                rowButtons: [{ text: "Delete", icon: 'fa-trash', color: '#F3565D', onClick: deleteRow }],
            });
    }

    return (
        <Popup show={show} onHide={onHide} size={'lg'} title={showAddSize == true ? 'Add size': 'Sizes'} >
            <Popup.Body>
                {showAddSize == true && <SelectListField {...getFieldProps('sizeSetId')} options={sizesets} />}
                {showAddSize == true && <SelectListField {...getFieldProps('sizeId')} options={sizes} />}
                {showAddSize == false &&
                    <DataTable
                        name='dtItemSizes'
                        tableData={tableData}
                        colInfos={colInfos}
                    />
                }
            </Popup.Body>
            <Popup.Footer>
                {showAddSize && <SaveButton {...getSubmitButtonProps()} text='Add' />}
            </Popup.Footer>
        </Popup>
    );
}
export default ItemSizesPopup;